import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getUploadUrl } from '../utils/uploadUrl';
import { getTaskDisplayStatusClass } from '../utils/taskStatus';
import { formatTaskDisplayStatus } from '../utils/taskLabels';
import { isTaskRemoved } from '../utils/taskFilters';
import TaskBillingStatusBadges from './TaskBillingStatusBadges';
import { formatTaskRecurrence } from '../utils/recurrenceWeekdays';
import { canAccessFeature } from '../utils/planAccess';
import './WorkGroupSelect.css';

function TaskDetailHeader({
  task,
  user,
  isDraft,
  canEdit,
  canEditDraftAsEmployee,
  canAdminDraft,
  editing,
  bannerUrl,
  canClaim,
  claiming,
  onClaim,
  onEdit,
  canRemove,
  canRestore,
  canPermanentlyDelete,
  permanentDeleteSubmitting,
  onRemove,
  onRestore,
  onPermanentlyDelete
}) {
  const { t } = useTranslation(['tasks', 'common']);
  const navigate = useNavigate();
  const locationLabel = [task.location_name, task.location_address].filter(Boolean).join(', ');
  const customerLabel = task.customer_name || null;
  const removed = isTaskRemoved(task);
  const statusLabel = formatTaskDisplayStatus(task, t, { treatDraft: isDraft });
  const statusClass = getTaskDisplayStatusClass(task, { treatDraft: isDraft });

  return (
    <div className="task-detail-chrome">
      <div className="task-detail-topbar">
        <button
          className="task-back-button"
          onClick={() => navigate('/tasks')}
          aria-label={t('tasks:backAria')}
        >
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <div className="task-detail-breadcrumb" aria-hidden="true">
          <span>{t('tasks:pageTitle')}</span>
          <span className="task-detail-breadcrumb-sep">/</span>
          <span className="task-detail-breadcrumb-current">{task.title}</span>
        </div>
        <div className="task-detail-topbar-actions">
          {canRemove && (
            <button
              type="button"
              className="task-edit-button"
              onClick={onRemove}
              aria-label={t('tasks:removeTaskAria')}
              title={t('tasks:removeTask')}
            >
              <span className="material-symbols-outlined">delete</span>
            </button>
          )}
          {canRestore && (
            <button
              type="button"
              className="task-edit-button"
              onClick={onRestore}
              aria-label={t('tasks:restoreTaskAria')}
              title={t('tasks:restoreTask')}
            >
              <span className="material-symbols-outlined">undo</span>
            </button>
          )}
          {canPermanentlyDelete && (
            <button
              type="button"
              className="task-edit-button"
              onClick={onPermanentlyDelete}
              disabled={permanentDeleteSubmitting}
              aria-label={t('tasks:permanentlyDeleteTaskAria')}
              title={t('tasks:permanentlyDeleteTask')}
            >
              <span className="material-symbols-outlined">delete_forever</span>
            </button>
          )}
          {canEdit && (
            <button
              className="task-edit-button"
              onClick={onEdit}
              aria-label={t('tasks:editTaskAria')}
            >
              <span className="material-symbols-outlined">edit</span>
            </button>
          )}
        </div>
      </div>

      {bannerUrl ? (
        <div className="task-banner">
          <img src={getUploadUrl(bannerUrl)} alt="" />
        </div>
      ) : (
        <div className="task-banner task-banner--placeholder" aria-hidden="true" />
      )}

      <div className="task-detail-header">
        <div className="task-header-content">
          <div className="task-title-row">
            <h1 className="task-title">{task.title}</h1>
            <span className={`${statusClass} status-badge--spaced`}>
              {statusLabel}
            </span>
            {canAccessFeature(user, 'taskWorkGroups') && task.work_group_name && (
              <span className="tasks-work-group-badge" title={task.work_group_name}>
                {task.work_group_name}
              </span>
            )}
            <TaskBillingStatusBadges task={task} />
          </div>
          {locationLabel && (
            <div className="task-location-info">
              <span className="material-symbols-outlined" aria-hidden="true">location_on</span>
              {locationLabel}
            </div>
          )}
          {customerLabel && (
            <div className="task-location-info">
              <span className="material-symbols-outlined" aria-hidden="true">contacts</span>
              {customerLabel}
            </div>
          )}
          {task.recurrence_enabled && (
            <div className="task-location-info">
              <span className="material-symbols-outlined" aria-hidden="true">event_repeat</span>
              {t('tasks:recurrenceOnLabel')}: {formatTaskRecurrence(task, t)}
            </div>
          )}
          {removed && (
            <div className="task-detail-removed-banner">
              <p>{t('tasks:removedBanner')}</p>
              {task.remove_reason && (
                <p>{t('tasks:removedReasonLabel', { reason: task.remove_reason })}</p>
              )}
              <div className="task-detail-removed-actions">
                {canRestore && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={onRestore}>
                    {t('tasks:restoreTask')}
                  </button>
                )}
                {canPermanentlyDelete && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={onPermanentlyDelete}
                    disabled={permanentDeleteSubmitting}
                  >
                    {permanentDeleteSubmitting
                      ? t('tasks:deletingTask')
                      : t('tasks:permanentlyDeleteTask')}
                  </button>
                )}
              </div>
            </div>
          )}
          {canEditDraftAsEmployee && (
            <p className="task-detail-draft-notice">
              {t('tasks:draftWaitingNotice')}
            </p>
          )}
          {canAdminDraft && !editing && (
            <div className="task-detail-draft-actions">
              <button type="button" className="btn btn-primary" onClick={onEdit}>
                {t('tasks:reviewAndPublish')}
              </button>
            </div>
          )}
          {canClaim && (
            <div className="task-claim-row">
              <button
                type="button"
                className="btn btn-primary task-claim-button"
                onClick={onClaim}
                disabled={claiming}
              >
                <span className="material-symbols-outlined" aria-hidden="true">handshake</span>
                {t('tasks:claim')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskDetailHeader;
